import { useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import {
  DataType,
  GraphConfig,
  GraphGlobalMetric,
} from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { Skeleton, useTheme } from "@mui/material";
import { Stack } from "@mui/system";
import { useGetOrgId } from "@src/api/organizations/get-organization";
import { GraphHeaderToolbar } from "@src/features/analytics/baseDashboard/chartComponents/GraphHeaderToolbar";
import { HeaderDetails } from "@src/features/analytics/baseDashboard/chartComponents/HeaderDetails";
import { UpliftImpactWrapper } from "@src/features/analytics/baseDashboard/UpliftImpactWrapper";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { useReadOnlyMode } from "@src/features/publicShareLinks/hooks/useReadOnlyMode";
import { getExperienceAnalyticsFilters } from "@src/store/slices/dashboardFilters";
import { selectedExperience } from "@src/store/slices/experiences";
import { GraphMetricData } from "@src/types/analytics";

interface GraphHeaderProps {
  isDataLoading: boolean;
  groupMetricData?: GraphMetricData;
  config: GraphConfig;
  metricFilterIndex?: number;
  globalMetrics?: GraphGlobalMetric[];
  testDuration: number;
}

export const GraphHeader = ({
  groupMetricData,
  config,
  metricFilterIndex,
  isDataLoading,
  globalMetrics,
  testDuration,
}: GraphHeaderProps) => {
  const { isReadOnly } = useReadOnlyMode();
  const [hover, setHover] = useImmer(false);
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
  const orgId = useGetOrgId();
  const experience = useSelector(selectedExperience);
  const graphValueFormatter = useGraphValueFormatter();
  const igTheme = useTheme();
  const hasNoData =
    !isDataLoading &&
    (!groupMetricData ||
      groupMetricData.length < 1 ||
      groupMetricData.length === 0);

  return (
    <Stack
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      sx={{
        position: "relative",
      }}
    >
      <Stack gap={3}>
        <HeaderDetails config={config} metricFilterIndex={metricFilterIndex} />

        {!hasNoData &&
          config?.isImpactGraph &&
          dashboardFilters.compareGroupA &&
          dashboardFilters.compareGroupB && (
            <UpliftImpactWrapper
              config={config}
              data={groupMetricData}
              isLoading={
                !dashboardFilters.compareGroupB ||
                !dashboardFilters.compareGroupA ||
                isDataLoading
              }
              isSharing={false}
              testDuration={testDuration}
            />
          )}

        {config?.showGlobalMetrics && globalMetrics && (
          <Stack direction={"row"} spacing={4}>
            {globalMetrics.map((metric, index) => (
              <Stack spacing={1} key={index}>
                {metric.label && (
                  <IgTypography
                    variant="body2"
                    color={igTheme.palette.secondary.main}
                  >
                    {metric.label}
                  </IgTypography>
                )}

                {isDataLoading ? (
                  <Skeleton width={80} height={30} />
                ) : (
                  <IgTypography variant={"h1"} fontWeight={600}>
                    {graphValueFormatter({
                      isAxisTick: false,
                      value: metric.value || 0,
                      configDataTypes: metric.dataType
                        ? ([metric.dataType] as DataType[])
                        : config?.dataTypes,
                    })}
                  </IgTypography>
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>

      {/* GraphHeaderToolbar has mutation actions - hide in read-only mode */}
      {experience && !hasNoData && config?.isImpactGraph && !isReadOnly && (
        <GraphHeaderToolbar
          config={config}
          experienceId={experience.id!}
          orgId={orgId}
          hover={hover}
        />
      )}
    </Stack>
  );
};
